/**
 * ReplyComposer — bottom input area of the open chat (Iter 140, Feb 2026).
 *
 * Owns: textarea, send button, attachment popover, schedule popover, quick
 * replies, AI-suggest button, payment-link QR helpers. State stays in
 * ChatsPage (replyText, replyToMsg, attachment, schedule fields, ...).
 *
 * Final piece of the iter-135/138/140 ChatsPage scalability split.
 */
import { Clock, Loader2, Paperclip, QrCode, Send, Sparkles, Zap, X } from "lucide-react";

export default function ReplyComposer({
  inputRef,
  attachInputRef,
  // text
  replyText, setReplyText,
  replyToMsg, setReplyToMsg,
  // send
  handleSendMessage, sending,
  handleKeyDown, handlePaste,
  // attachment
  attachFile, setAttachFile, setAttachment,
  attachPreviewUrl, setAttachPreviewUrl,
  attachCaption, setAttachCaption,
  sendingAttach, handleSendAttachment,
  // schedule
  showScheduleModal, setShowScheduleModal,
  scheduleType, setScheduleType,
  scheduleDate, setScheduleDate,
  scheduleTime, setScheduleTime,
  handleScheduleMessage,
  // quick replies + AI
  showQuickReplies, setShowQuickReplies,
  aiLoading, handleAiSuggest,
  sendingPaymentQR, handleSendPaymentQR,
}) {
  return (
    <>
                  <div className="border-t border-[--border] bg-transparent px-5 py-3" data-testid="reply-bar">
                    {/* Reply-to quote */}
                    {replyToMsg && (
                      <div className="flex items-start gap-2 mb-2 p-2 bg-blue-50 rounded-lg border-l-3 border-blue-400" data-testid="reply-to-quote">
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-semibold text-blue-700">{replyToMsg.direction === "incoming" ? "Customer" : "You"}</p>
                          <p className="text-[11px] text-blue-600 truncate">{(replyToMsg.content || "[Media]").slice(0, 120)}</p>
                        </div>
                        <button onClick={() => setReplyToMsg(null)} className="text-blue-400 hover:text-blue-600 flex-shrink-0 mt-0.5"><X size={14} /></button>
                      </div>
                    )}
                    {/* Attachment preview - WhatsApp style */}
                    {attachFile && (
                      <div className="mb-2 rounded-xl border border-[--border] bg-gray-50 overflow-hidden" data-testid="attachment-preview">
                        {/* Visual preview for images/videos */}
                        {attachPreviewUrl && attachFile.type.startsWith("image/") && (
                          <div className="relative bg-black/5 flex items-center justify-center max-h-[240px] overflow-hidden">
                            <img src={attachPreviewUrl} alt="Preview" className="max-h-[240px] max-w-full object-contain" data-testid="attachment-preview-image" />
                          </div>
                        )}
                        {attachPreviewUrl && attachFile.type.startsWith("video/") && (
                          <div className="relative bg-black flex items-center justify-center max-h-[240px] overflow-hidden">
                            <video src={attachPreviewUrl} controls className="max-h-[240px] max-w-full" data-testid="attachment-preview-video" />
                          </div>
                        )}
                        {/* Non-media file icon */}
                        {!attachPreviewUrl && (
                          <div className="flex items-center justify-center py-4 bg-blue-50">
                            <Paperclip size={24} className="text-blue-500" />
                          </div>
                        )}
                        {/* File info + caption + actions */}
                        <div className="px-3 py-2 flex items-center gap-2 bg-white border-t border-[--border]">
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-medium text-[--text-primary] truncate">{attachFile.name}</p>
                            <p className="text-[10px] text-[--text-muted]">{(attachFile.size / 1024).toFixed(1)} KB</p>
                          </div>
                          <input type="text" value={attachCaption} onChange={(e) => setAttachCaption(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSendAttachment(); } }}
                            placeholder="Add a caption..."
                            className="flex-1 bg-[--bg-cream] border border-[--border] rounded-lg px-2.5 py-1.5 text-xs outline-none focus:ring-1 focus:ring-[--accent]" data-testid="attach-caption-input" />
                          <button onClick={handleSendAttachment} disabled={sendingAttach}
                            className="w-8 h-8 rounded-full bg-[--accent] text-white flex items-center justify-center hover:opacity-90 transition-opacity flex-shrink-0" data-testid="send-attach-btn">
                            {sendingAttach ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                          </button>
                          <button onClick={() => setAttachment(null)} className="text-[--text-muted] hover:text-red-500 transition-colors flex-shrink-0" data-testid="cancel-attach-btn"><X size={16} /></button>
                        </div>
                      </div>
                    )}
                    <div className="flex items-end gap-2">
                      <div className="flex-1 flex items-end gap-1 bg-white rounded-full px-2 py-1 border-2 border-[--border] focus-within:border-[--accent-soft] transition-all" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
                      <button onClick={handleAiSuggest} disabled={!!aiLoading} className="flex-shrink-0 mx-auto bg-gradient-to-r from-purple-100 to-blue-100 hover:from-purple-200 hover:to-blue-200 text-purple-700 text-[10px] font-bold px-3 py-1.5 rounded-full flex items-center gap-1 border border-purple-200 disabled:opacity-50" title="AI Draft reply (into your text box)" data-testid="ai-suggest-reply-btn">
                        {aiLoading === "suggest" ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                        AI Draft
                      </button>
                      <button
                        onClick={() => setShowQuickReplies(v => !v)}
                        className={`flex-shrink-0 text-[10px] font-bold px-3 py-1.5 rounded-full flex items-center gap-1 border transition-colors ${showQuickReplies ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200"}`}
                        title="Saved replies / templates"
                        data-testid="toggle-quick-replies-btn"
                      >
                        <Zap size={11} />
                        Saved
                      </button>
                      <input type="file" ref={attachInputRef} className="hidden" accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx" onChange={(e) => { if (e.target.files[0]) setAttachment(e.target.files[0]); e.target.value = ""; }} data-testid="attach-file-input" />
                      <button onClick={() => attachInputRef.current?.click()} className="btn-icon flex-shrink-0" title="Attach file" data-testid="attach-file-btn"><Paperclip size={16} /></button>
                      <button onClick={handleSendPaymentQR} disabled={sendingPaymentQR} className="btn-icon flex-shrink-0" title="Send Payment QR" data-testid="send-qr-btn">
                        {sendingPaymentQR ? <Loader2 size={16} className="animate-spin" /> : <QrCode size={16} className="text-green-600" />}
                      </button>
                      <div className="relative">
                        <button onClick={() => setShowScheduleModal(!showScheduleModal)} className={`btn-icon flex-shrink-0 ${showScheduleModal ? "bg-[--accent-pale] text-[--accent]" : ""}`} title="Schedule message or reminder" data-testid="schedule-btn">
                          <Clock size={16} className="text-blue-500" />
                        </button>
                        {showScheduleModal && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setShowScheduleModal(false)} />
                            <div className="absolute bottom-full left-0 mb-2 z-50 bg-white rounded-xl shadow-2xl border border-[--border] w-72 p-3 animate-scale-in" data-testid="schedule-modal">
                              <p className="text-xs font-semibold text-[--text-primary] mb-2">Schedule</p>
                              <div className="flex gap-1.5 mb-2">
                                <button onClick={() => setScheduleType("whatsapp")} className={`flex-1 text-[10px] py-1.5 rounded-lg font-medium ${scheduleType === "whatsapp" ? "bg-green-100 text-green-700 border border-green-300" : "bg-gray-50 text-gray-500 border border-gray-200"}`} data-testid="schedule-type-whatsapp">
                                  Send WhatsApp
                                </button>
                                <button onClick={() => setScheduleType("reminder")} className={`flex-1 text-[10px] py-1.5 rounded-lg font-medium ${scheduleType === "reminder" ? "bg-blue-100 text-blue-700 border border-blue-300" : "bg-gray-50 text-gray-500 border border-gray-200"}`} data-testid="schedule-type-reminder">
                                  Agent Reminder
                                </button>
                              </div>
                              <p className="text-[9px] text-[--text-muted] mb-2">{scheduleType === "whatsapp" ? "Message will be sent to customer at scheduled time" : "You'll get a reminder to follow up with this customer"}</p>
                              <div className="grid grid-cols-2 gap-1.5 mb-2">
                                <input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5" data-testid="schedule-date" />
                                <input type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5" data-testid="schedule-time" />
                              </div>
                              <button onClick={handleScheduleMessage} className="w-full btn-primary text-xs py-1.5" data-testid="confirm-schedule-btn">
                                {scheduleType === "whatsapp" ? "Schedule Message" : "Set Reminder"}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                      <textarea ref={inputRef} value={replyText} onChange={(e) => {
                          setReplyText(e.target.value);
                          e.target.style.height = "auto";
                          e.target.style.height = Math.min(e.target.scrollHeight, 200) + "px";
                        }} onKeyDown={handleKeyDown} onPaste={handlePaste} placeholder="Type a message..." rows={1}
                        className="flex-1 bg-transparent border-none rounded-lg px-2 py-2 text-[13px] focus:ring-0 outline-none placeholder:text-[--text-muted] resize-none max-h-[200px] overflow-y-auto"
                        style={{ minHeight: "38px" }} data-testid="reply-input" />
                      </div>
                      <button onClick={handleSendMessage} disabled={!replyText.trim() || sending}
                        className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all ${replyText.trim() && !sending ? "text-white shadow-md hover:scale-105" : "bg-[--border-dark] text-[--text-muted]"}`}
                        style={replyText.trim() && !sending ? { background: "linear-gradient(135deg, #EA580C, #DC2626)", boxShadow: "0 3px 12px rgba(234,88,12,0.3)" } : {}}
                        data-testid="send-message-btn">
                        {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                      </button>
                    </div>
                  </div>
    </>
  );
}
